import { IFileResponse } from '@api/client';

export enum ImagePreviewSize {
  Small = 210,
  Thumbnail = 640,
  Medium = 1440,
}

const normalizeUrl = (url: string | null | undefined): string | null => url?.trim() || null;

const getVariantUrl = (
  file?: IFileResponse | null,
  targetSize: number = ImagePreviewSize.Medium
): string | null => {
  const variants = file?.variants;
  const avatar = normalizeUrl(variants?.avatar?.url);
  const thumbnail = normalizeUrl(variants?.thumbnail?.url);
  const medium = normalizeUrl(variants?.medium?.url);
  if (targetSize <= ImagePreviewSize.Small) return avatar || thumbnail || medium;
  if (targetSize <= ImagePreviewSize.Thumbnail) return thumbnail || medium || avatar;
  return medium || thumbnail || avatar;
};

const isGifImage = (file: IFileResponse | null | undefined, fallbackUrl?: string | null): boolean =>
  /^image\/gif(?:\s*;|$)/i.test(normalizeUrl(file?.type?.name) ?? '')
  || [file?.name, file?.origin?.url, file?.url, fallbackUrl]
    .some(value => normalizeUrl(value)?.split(/[?#]/, 1)[0].toLowerCase().endsWith('.gif'));

export const getImageViewerUrl = (
  file?: IFileResponse | null,
  fallbackUrl?: string | null
): string | null => {
  const originalUrl = normalizeUrl(file?.origin?.url) || normalizeUrl(file?.url) || normalizeUrl(fallbackUrl);
  // Server resizes use GD and do not preserve GIF animation.
  return isGifImage(file, fallbackUrl) ? originalUrl || getVariantUrl(file) : getVariantUrl(file) || originalUrl;
};

export const getImageThumbnailUrl = (
  file?: IFileResponse | null,
  targetSize: number = ImagePreviewSize.Thumbnail,
  fallbackUrl?: string | null
): string | null => getVariantUrl(file, targetSize)
  || normalizeUrl(fallbackUrl)
  || normalizeUrl(file?.url)
  || normalizeUrl(file?.origin?.url);

export const getImageIconUrl = (file?: IFileResponse | null): string | null =>
  getImageThumbnailUrl(file, ImagePreviewSize.Small);

export const getAvatarPreviewUrl = (
  file: IFileResponse | null | undefined,
  targetSize: number,
  fallbackUrl?: string | null
): string | null => getImageThumbnailUrl(file, targetSize, fallbackUrl);
